/* eslint-disable quotes */
import { DirectionalHint, getTheme, Icon, Stack, StackItem, Text, TooltipHost } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import React from 'react';
import 'styles/helpers.css';
import { ILowerTopBarState } from './LowerTopBar.state';
import { ChangeArea, ChangeLocation, EnterCampus, EnterOffice, LevelElevator, Location } from 'kits/IconKit2';
import { IC } from 'components/SVGIcon';
import LowerHeaderDropdown from './components/LowerHeaderDropdown';
import { useNavigate } from 'react-router';
import { getUrlLocation } from 'kits/urlKit';
import { showTooltipForTruncatedText, truncateText } from 'kits/truncateTextKit';

export interface ILowerTopBarStyles {
    textColor?: string;
    backgroundColor?: string;
    barLogo?: string;
}

const LowerTopBarView: React.FC<ILowerTopBarState> = (props) => {
    const theme = getTheme();
    const routerHistory = useNavigate();
    const { isInDashboardLobby } = getUrlLocation();

    const {
        children,
        localProfile,
        joinedOrgs,
        orgId,
        logInAndRedirect,
        textColor,
        backgroundColor,
        showJoinedOrgsDropdown,
        isAnimating,
        l0AreaGroup,
        l1AreaGroup,
        handleDropdownToggle,
        showL0AreaGroupDropdown,
        showL1AreaGroupDropdown,
        showAreaDropdown,
        l0AndL1AGTitle,
        barTitle,
        showL1AGDropdown,
        handleRedirectionFromDropdown,
        areaListForDropdown,
        currentAreaTitleForDropdown,
        dropdownRef
    } = props;

    const truncatedBarTitle = truncateText(barTitle, 20);

    return (
        <div ref={dropdownRef}>
            <Stack aria-label="lower-topbar" disableShrink={true}>
                <Stack
                    aria-label="lower-topbar-color"
                    horizontal
                    verticalAlign="center"
                    style={{
                        height: '48px',
                        minHeight: 48,
                        background: backgroundColor,
                        color: textColor,
                        padding: '0px 16px'
                    }}
                >
                    {/* Change Location(orgs) */}
                    <Stack horizontal verticalAlign="center">
                        <Stack horizontal verticalAlign="center" styles={{ root: { cursor: 'pointer' } }}>
                            <TooltipHost content={showTooltipForTruncatedText(barTitle, truncatedBarTitle)} directionalHint={DirectionalHint.bottomCenter}>
                                <Stack
                                    horizontal
                                    verticalAlign="center"
                                    onClick={() => {
                                        if (isInDashboardLobby) {
                                            handleDropdownToggle('joinedOrgs');
                                        } else {
                                            routerHistory('/dashboard');
                                        }
                                    }}
                                >
                                    <IC variant={'custom'} size={32} iconColor={textColor}>
                                        <EnterCampus />
                                    </IC>
                                    {/* Campus Label */}
                                    <Text
                                        style={{
                                            marginLeft: 16,
                                            color: textColor,
                                            fontWeight: 'bold',
                                            width: 140
                                        }}
                                    >
                                        {truncatedBarTitle || 'Campus'}
                                    </Text>
                                </Stack>
                            </TooltipHost>

                            <Icon
                                iconName={'ChevronDownMed'}
                                styles={{
                                    root: {
                                        fontSize: '14px',
                                        marginTop: 2,
                                        marginLeft: 8,
                                        color: textColor,
                                        cursor: 'pointer',
                                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                    }
                                }}
                                style={{
                                    transform: showJoinedOrgsDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                                }}
                                onClick={() => handleDropdownToggle('joinedOrgs')}
                            />
                        </Stack>

                        {/* Joined orgs dropdown */}
                        {(showJoinedOrgsDropdown || isAnimating) && joinedOrgs.length > 1 && (
                            <Stack
                                style={{
                                    position: 'absolute',
                                    top: 95,
                                    left: 25,
                                    width: 200,
                                    zIndex: 100,
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                    backgroundColor: theme.palette.neutralLighter,
                                    color: '#000',
                                    overflow: 'hidden',
                                    transform: showJoinedOrgsDropdown ? 'scaleY(1)' : 'scaleY(0)',
                                    transformOrigin: 'top',
                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    opacity: showJoinedOrgsDropdown ? 1 : 0.8,
                                    transitionProperty: 'transform, opacity'
                                }}
                            >
                                {/* Change location */}
                                <Stack
                                    horizontal
                                    verticalAlign="center"
                                    tokens={{ childrenGap: 8 }}
                                    style={{ backgroundColor: theme.palette.blackTranslucent40, width: '100%', height: '58px', padding: 16 }}
                                >
                                    <IC variant={'custom'} size={24} iconColor={'#fff'}>
                                        <ChangeLocation />
                                    </IC>
                                    <Text style={{ color: '#fff' }}>{IvicosStrings.changeLocationTo}</Text>
                                </Stack>

                                {/* Joined orgs */}
                                <Stack style={{ padding: 16, maxHeight: '300px', overflowY: 'auto' }}>
                                    {joinedOrgs.map((org, index) => {
                                        const isSelected = org.key === orgId;
                                        return (
                                            <TooltipHost
                                                content={showTooltipForTruncatedText(org.text, truncateText(org.text, 24))}
                                                directionalHint={DirectionalHint.bottomCenter}
                                                key={org.key}
                                            >
                                                <Stack
                                                    key={org.key}
                                                    horizontal
                                                    verticalAlign="center"
                                                    tokens={{
                                                        childrenGap: 8
                                                    }}
                                                    styles={{
                                                        root: {
                                                            padding: '12px 4px',
                                                            transition: 'background-color 0.2s ease',
                                                            transform: showJoinedOrgsDropdown ? 'translateY(0)' : `translateY(-${(index + 1) * 10}px)`,
                                                            opacity: showJoinedOrgsDropdown ? 1 : 0,
                                                            transitionDelay: showJoinedOrgsDropdown ? `${index * 50}ms` : '0ms',
                                                            transitionDuration: '0.3s',
                                                            backgroundColor: isSelected ? theme.palette.themePrimary : 'transparent',
                                                            color: isSelected ? theme.palette.white : theme.palette.neutralPrimary,
                                                            fontWeight: isSelected ? '600' : 'normal',
                                                            cursor: 'pointer',

                                                            margin: '2px 0',
                                                            '&:hover': {
                                                                backgroundColor: isSelected ? theme.palette.themePrimary : theme.palette.neutralLight
                                                            }
                                                        }
                                                    }}
                                                    onClick={() => (isSelected ? routerHistory('/dashboard') : logInAndRedirect(org.key as string))}
                                                >
                                                    <StackItem>
                                                        <IC variant={isSelected ? 'light' : 'dark'} size={16}>
                                                            <Location />
                                                        </IC>
                                                    </StackItem>
                                                    <Text
                                                        styles={{
                                                            root: {
                                                                color: isSelected ? theme.palette.white : theme.palette.neutralPrimary,
                                                                fontWeight: isSelected ? '600' : 'normal'
                                                            }
                                                        }}
                                                    >
                                                        {truncateText(org.text, 24)}
                                                    </Text>
                                                </Stack>
                                            </TooltipHost>
                                        );
                                    })}
                                </Stack>
                            </Stack>
                        )}
                    </Stack>

                    {/* Level0(area group) */}
                    {l0AreaGroup.length > 0 && (
                        <LowerHeaderDropdown
                            dropdownType="l0AreaGroup"
                            handleDropdownToggle={handleDropdownToggle}
                            textColor={textColor}
                            headerLabel={{ icon: <LevelElevator />, title: l0AndL1AGTitle.l0Title, titleId: l0AndL1AGTitle.l0AGId }}
                            isDropdownOpen={showL0AreaGroupDropdown}
                            isAnimating={isAnimating}
                            items={l0AreaGroup}
                            itemIcon={<LevelElevator />}
                            dropdownLeftShift={255}
                            dropdownInitialItem={{ icon: <ChangeArea />, title: IvicosStrings.changeAreaGroupTo }}
                            redirectToLobby={{ icon: <EnterCampus />, title: 'Lobby' }}
                            handleRedirectionFromDropdown={(item, isLobby, type) => handleRedirectionFromDropdown(item, isLobby, type)}
                        />
                    )}

                    {/* Level1(area group) */}
                    {showL1AGDropdown && l1AreaGroup.length > 0 && (
                        <LowerHeaderDropdown
                            dropdownType="l1AreaGroup"
                            handleDropdownToggle={handleDropdownToggle}
                            textColor={textColor}
                            headerLabel={{ icon: <LevelElevator />, title: l0AndL1AGTitle.l1Title, titleId: l0AndL1AGTitle.l1AGId }}
                            isDropdownOpen={showL1AreaGroupDropdown}
                            isAnimating={isAnimating}
                            items={l1AreaGroup}
                            itemIcon={<LevelElevator />}
                            dropdownLeftShift={485}
                            dropdownInitialItem={{ icon: <ChangeArea />, title: IvicosStrings.changeAreaGroupTo }}
                            redirectToLobby={{ icon: <EnterCampus />, title: 'Lobby' }}
                            handleRedirectionFromDropdown={(item, isLobby, type) => handleRedirectionFromDropdown(item, isLobby, type)}
                        />
                    )}

                    {/* area dropdown */}
                    {areaListForDropdown.length > 0 && (
                        <LowerHeaderDropdown
                            dropdownType="areaDropdown"
                            handleDropdownToggle={handleDropdownToggle}
                            textColor={textColor}
                            headerLabel={{ icon: <EnterOffice />, title: currentAreaTitleForDropdown }}
                            isDropdownOpen={showAreaDropdown}
                            isAnimating={isAnimating}
                            items={areaListForDropdown}
                            itemIcon={<EnterOffice />}
                            dropdownLeftShift={l1AreaGroup.length > 0 ? 720 : l0AreaGroup.length > 0 ? 485 : 255}
                            dropdownInitialItem={{ icon: <EnterOffice />, title: IvicosStrings.enterOfficeOf }}
                            handleRedirectionFromDropdown={(item, isLobby, type) => handleRedirectionFromDropdown(item, isLobby, type)}
                        />
                    )}

                    {/* Content rendered here i.e. search person, invitation manager, navigator etc. */}
                    <Stack
                        key="content"
                        horizontal
                        grow
                        horizontalAlign="start"
                        verticalAlign="center"
                        style={{ height: '100%', marginLeft: 16, marginRight: 16 }}
                    >
                        {typeof children == 'function' ? children(textColor, true) : children}
                    </Stack>
                </Stack>
            </Stack>
        </div>
    );
};

export default LowerTopBarView;
